import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { Button, Loader, Modal } from '../../../../Components/uielements';
import { IntlMessages } from '../../../../helper/langHelper';
import {
  OutlineButton,
  ModalButtonWrapper,
} from '../../../../services/settings/appStyle/global.style';
import CompanyService from '../../../../services/bioplus-services/company';

const HelperText = styled.p`
  font-size: 14px;
  color: #fff;
  line-height: 1.6;
  font-weight: 400;
`;

const MessageContainer = styled.div`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 16px;
  margin: 20px 0;
`;

const LinkContainer = styled.div`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 16px;
  margin: 20px 0;
  position: relative;
`;

const MessageLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MessageDisplay = styled.textarea`
  width: 100%;
  min-height: 180px;
  max-height: 260px;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #fff;
  background: transparent;
  resize: vertical;
  box-sizing: border-box;
  white-space: pre-wrap;
  line-height: 1.5;
  overflow: auto;

  &:focus {
    outline: none;
    border-color: #0071e3;
  }
`;

const LinkLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const LinkTextArea = styled.textarea`
  width: 100%;
  min-height: 56px;
  max-height: 84px;
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #fff;
  background: transparent;
  resize: none;
  box-sizing: border-box;
  white-space: pre-wrap;
  overflow: hidden;
  line-height: 1.5;

  &:focus {
    outline: none;
    border-color: #0071e3;
  }
`;

const CopyButtonWrapper = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #ffebee;
  border: 1px solid #f44336;
  border-radius: 6px;
  color: #c62828;
  font-size: 13px;
  font-weight: 500;
  margin-top: 8px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const ModalWrapper = styled.div`
  display: grid;
  gap: 10px;
  padding: 10px 20px 30px;
  min-width: 800px;
`;

const InviteFriendModal = ({ onClose }) => {
  const [error, setError] = useState(null);
  const [inviteData, setInviteData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [messageCopied, setMessageCopied] = useState(false);

  const linkTextAreaRef = useRef(null);
  const messageTextAreaRef = useRef(null);

  const handleGenerateLink = async () => {
    try {
      setIsProcessing(true);
      setError(null);
      setCopied(false);
      setMessageCopied(false);

      const response = await CompanyService.inviteFriend({});
      const data = response?.data || response || {};

      setInviteData({
        invitationCode: data?.invitationCode || '',
        link: data?.link || data?.inviteLink || '',
        inviteMessage: data?.inviteMessage || '',
        expiryDuration: data?.expiryDuration || 0,
      });

      setIsProcessing(false);
    } catch (e) {
      setIsProcessing(false);
      setError(e?.response?.data?.message || 'Failed to generate invite link');
    }
  };

  const copyText = async (text, setter) => {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      setTimeout(() => setter(false), 2000);
    } catch (err) {
      setError('Failed to copy text');
    }
  };

  const handleCopyLink = () => {
    if (!linkTextAreaRef.current) return;
    copyText(linkTextAreaRef.current.value, setCopied);
  };

  const handleCopyMessage = () => {
    if (!messageTextAreaRef.current) return;
    copyText(messageTextAreaRef.current.value, setMessageCopied);
  };

  return (
    <Modal isOpen={true} title="Invite Friends to AGManager™">
      <ModalWrapper>
        {!inviteData && !error ? (
          <>
            <HelperText>
              Click the button below to generate an invitation. The full message will be shown, and
              you can copy the invitation text or only the link.
            </HelperText>
          </>
        ) : null}

        {inviteData && !error ? (
          <>
            <MessageContainer>
              <MessageLabel>Full Invitation Message</MessageLabel>
              <MessageDisplay
                ref={messageTextAreaRef}
                readOnly
                value={inviteData.inviteMessage.replace(/\\n/g, '\n').replace(/\\t/g, '\t')}
              />
              <CopyButtonWrapper>
                <Button varient="normal" onClick={handleCopyMessage}>
                  {messageCopied ? '✓ Copied!' : 'Copy Message'}
                </Button>
              </CopyButtonWrapper>
            </MessageContainer>

            <LinkContainer>
              <LinkLabel>Invitation Link</LinkLabel>
              <LinkTextArea ref={linkTextAreaRef} readOnly value={inviteData.link} />
              <CopyButtonWrapper>
                <Button varient="normal" onClick={handleCopyLink}>
                  {copied ? '✓ Copied!' : 'Copy Link'}
                </Button>
              </CopyButtonWrapper>
            </LinkContainer>

            <HelperText style={{ fontSize: '12px', opacity: 0.8 }}>
              <strong>Code:</strong> {inviteData.invitationCode || 'N/A'}{' '}
              {inviteData.expiryDuration ? (
                <>
                  | <strong>Expires in:</strong> {inviteData.expiryDuration} hours
                </>
              ) : null}
            </HelperText>
          </>
        ) : null}

        {error ? (
          <>
            <ErrorMessage>
              <span>✕</span>
              <span>{error}</span>
            </ErrorMessage>
          </>
        ) : null}

        <ModalButtonWrapper style={{ marginTop: '24px' }}>
          {!inviteData && (
            <ButtonGroup>
              <Button varient="normal" onClick={handleGenerateLink} disabled={isProcessing}>
                {isProcessing ? (
                  <>
                    <div style={{ paddingRight: '8px', display: 'inline-block' }}>
                      <Loader size="small" isNotRequiredLoadingText={true} />
                    </div>
                    <IntlMessages id="button.text.processing" defaultMessage="Processing..." />
                  </>
                ) : (
                  'Generate Invite'
                )}
              </Button>
            </ButtonGroup>
          )}
          {error && (
            <ButtonGroup style={{ marginTop: '16px' }}>
              <Button varient="normal" onClick={handleGenerateLink} disabled={isProcessing}>
                {isProcessing ? (
                  <>
                    <div style={{ paddingRight: '8px', display: 'inline-block' }}>
                      <Loader size="small" isNotRequiredLoadingText={true} />
                    </div>
                    <IntlMessages id="button.text.processing" defaultMessage="Processing..." />
                  </>
                ) : (
                  'Try Again'
                )}
              </Button>
            </ButtonGroup>
          )}
          <OutlineButton onClick={onClose}>
            <IntlMessages id="modal.button.close" defaultMessage="Close" />
          </OutlineButton>
        </ModalButtonWrapper>
      </ModalWrapper>
    </Modal>
  );
};

export default InviteFriendModal;
